/**
 * Cliente API centralizado con manejo de autenticación
 * Detecta tokens expirados y redirige al login automáticamente
 */
import { appConfig } from "@/config/app-config";

// Evento global para notificar cuando el token expira
export const AUTH_EXPIRED_EVENT = "auth:expired";

// Evento global para notificar cuando el backend devuelve 403 (permisos insuficientes)
export const PERMISSION_DENIED_EVENT = "permissions:denied";

/**
 * Dispara un evento global cuando el token expira
 */
export function notifyAuthExpired() {
  window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT));
}

/**
 * Dispara un evento global cuando el backend devuelve 403
 * Incluye la URL de la petición para diagnóstico
 */
export function notifyPermissionDenied(requestUrl: string) {
  window.dispatchEvent(
    new CustomEvent(PERMISSION_DENIED_EVENT, { detail: { requestUrl } })
  );
}

/**
 * Obtiene el token de autenticación del sessionStorage
 */
export function getAuthToken(): string | null {
  if (typeof window === "undefined") return null;
  try {
    // Intentar con la clave principal
    const oidcKey = `oidc.user:${appConfig.oidc.authority}:${appConfig.oidc.clientId}`;
    let oidcData = sessionStorage.getItem(oidcKey);
    if (oidcData) {
      const parsed = JSON.parse(oidcData);
      if (parsed.access_token) {
        return parsed.access_token;
      }
    }
    // Si no encontramos, buscar cualquier clave que empiece con oidc.user:
    const allKeys = Object.keys(sessionStorage);
    for (const key of allKeys) {
      if (key.startsWith("oidc.user:")) {
        const data = sessionStorage.getItem(key);
        if (data) {
          try {
            const parsed = JSON.parse(data);
            if (parsed.access_token) {
              return parsed.access_token;
            }
          } catch {
            // Continuar con la siguiente clave
          }
        }
      }
    }
  } catch {
    // Error obteniendo el token
  }
  return null;
}

/**
 * Wrapper de fetch que maneja automáticamente el 401
 */
export async function apiFetch(
  url: string,
  options: RequestInit = {}
): Promise<Response> {
  const token = getAuthToken();
  
  const isMultipart = options.body instanceof FormData;
  const defaultHeaders: HeadersInit = {
    Authorization: token ? `Bearer ${token}` : "",
  };
  if (!isMultipart) {
    defaultHeaders["Content-Type"] = "application/json";
  }

  const response = await fetch(url, {
    ...options,
    headers: {
      ...defaultHeaders,
      ...(options.headers || {}),
    },
  });

  // ✅ Detectar token expirado (401 Unauthorized)
  if (response.status === 401) {
    console.warn("[API Client] Token expirado o inválido (401). Notificando...");
    notifyAuthExpired();
  }

  // ✅ Detectar permisos insuficientes (403 Forbidden)
  if (response.status === 403) {
    console.warn("[API Client] Permiso denegado (403) para:", url);
    notifyPermissionDenied(url);
  }

  return response;
}

// ─── Interceptor global de fetch para detectar 403 en TODAS las llamadas ────
// Esto cubre vistas que usan fetch() directo sin pasar por apiFetch/useFetch.
// Excluye las URLs de carga de permisos para evitar loops.

const _originalFetch = window.fetch.bind(window);
const PERMISSION_LOADING_PATTERNS = [
  "/permissions?",               // catálogo de permisos
  "/permissions/role-templates", // plantillas de rol
  "/permissions/reset",          // reset de permisos
  "/identity/api/users",         // getUserByEmail (usado durante carga de permisos)
];

window.fetch = async function interceptedFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const response = await _originalFetch(input, init);

  if (response.status === 403) {
    const url = typeof input === "string" ? input : input instanceof URL ? input.href : (input as Request).url;
    // Solo notificar si es una llamada a nuestra API y NO es carga de permisos
    const isOurApi = url.includes(appConfig.apiUrlBase) || url.includes("/identity/api/");
    const isPermissionLoading = PERMISSION_LOADING_PATTERNS.some((p) => url.includes(p));
    if (isOurApi && !isPermissionLoading) {
      console.warn("[Fetch Interceptor] 403 detectado para:", url);
      notifyPermissionDenied(url);
    }
  }

  return response;
};

/**
 * Verificar si el token actual está expirado
 */
export function isTokenExpired(): boolean {
  if (typeof window === "undefined") return true;
  try {
    const oidcKey = `oidc.user:${appConfig.oidc.authority}:${appConfig.oidc.clientId}`;
    const oidcData = sessionStorage.getItem(oidcKey);
    if (!oidcData) return true;
    
    const parsed = JSON.parse(oidcData);
    if (!parsed.expires_at) return false;
    
    // Verificar si el token expira en los próximos 60 segundos
    const expiresAt = parsed.expires_at * 1000; // convertir a ms
    return Date.now() >= expiresAt - 60000;
  } catch {
    return true;
  }
}
